import React, { useState, useCallback, useRef } from 'react';
import { Button, Card } from '@etherisc/ui-kit';
import { getUniversalDocumentRequirements } from '../../config/countries/index';
import { DocumentRequirement } from '../../config/countries/types';
import { FarmerDocument } from '../../types/farmer';
import { UploadIcon, FileIcon, TrashIcon, CheckCircleIcon, AlertCircleIcon, EyeIcon, DownloadIcon } from 'lucide-react';
import { farmerService } from '../../services/farmerService';
import { formatFileSize } from '../../utils/formatters';
import { DocumentPreviewModal } from '../common/DocumentPreviewModal';
import { shouldPreviewAsImage } from '../../utils/fileUtils';

interface DocumentUploadProps {
  countryCode: string;
  documents: FarmerDocument[];
  onChange: (documents: FarmerDocument[]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
  farmerId?: string; // Optional for auto-updating farmer record
}

export function DocumentUpload({
  countryCode,
  documents,
  onChange,
  disabled = false,
  errors = {},
  farmerId
}: DocumentUploadProps) {
  const [dragOver, setDragOver] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState<FarmerDocument | null>(null);
  const [imagePreview, setImagePreview] = useState<{
    isOpen: boolean;
    document: FarmerDocument | null;
  }>({ isOpen: false, document: null });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const requirements = getUniversalDocumentRequirements();
  
  // Keep countryCode for backward compatibility and potential future use
  // Currently unused as we use universal document requirements
  void countryCode;

  const safeDocuments = Array.isArray(documents) ? documents : [];
  



  const validateFile = (file: File, requirement: DocumentRequirement) => {
    const errors: string[] = [];

    // Check file type
    if (!requirement.acceptedTypes.includes(file.type)) {
      const acceptedExtensions = requirement.acceptedTypes
        .map(type => type.split('/')[1])
        .join(', ');
      errors.push(`File type not supported. Accepted: ${acceptedExtensions}`);
    }

    // Check file size
    if (file.size > requirement.maxSize) {
      errors.push(`File too large. Maximum size: ${formatFileSize(requirement.maxSize)}`);
    }

    return errors;
  };

  const uploadFile = useCallback(async (file: File, requirement: DocumentRequirement) => {
    try {
      const result = await farmerService.uploadDocument(file, requirement.type, farmerId);
      const newDocument = result.document;

      const updatedDocuments = [...safeDocuments, newDocument];
      onChange(updatedDocuments);
    } catch (error) {
      console.error('Upload failed:', error);
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [safeDocuments, onChange, farmerId]);

  const downloadDocument = useCallback(async (doc: FarmerDocument) => {
    try {
      const response = await fetch(doc.url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = doc.filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);

  const viewDocument = useCallback((document: FarmerDocument) => {
    // Check if it's an image or PDF that should be previewed in modal
    const isPDF = document.mimeType === 'application/pdf' || document.filename.toLowerCase().endsWith('.pdf');
    
    if (shouldPreviewAsImage(document) || isPDF) {
      setImagePreview({ isOpen: true, document });
    } else {
      // Open other document types in new tab/window
      window.open(document.url, '_blank');
    }
  }, []);

  const handleDeleteClick = useCallback((document: FarmerDocument) => {
    setDeletingDocument(document);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingDocument) {
      return;
    }

    if (farmerId) {
      try {
        await farmerService.deleteDocument(farmerId, deletingDocument.id);
      } catch (error) {
        console.error('Delete failed:', error);
        alert(`Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        setDeletingDocument(null);
        return;
      }
    }

    const updatedDocuments = safeDocuments.filter(doc => doc.id !== deletingDocument.id);
    onChange(updatedDocuments);
    setDeletingDocument(null);
  }, [deletingDocument, farmerId, safeDocuments, onChange]);

  const handleDeleteCancel = useCallback(() => {
    setDeletingDocument(null);
  }, []);

  const handleFileSelect = useCallback(async (files: FileList | null, requirement: DocumentRequirement) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const validationErrors = validateFile(file, requirement);

    if (validationErrors.length > 0) {
      // Show validation errors
      alert(validationErrors.join('\n'));
      return;
    }

    await uploadFile(file, requirement);
  }, [uploadFile]);

  const handleDrop = useCallback((e: React.DragEvent, requirement: DocumentRequirement) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files, requirement);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const openFileDialog = useCallback((requirement: DocumentRequirement) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = requirement.acceptedTypes.join(',');
      fileInputRef.current.onchange = (e) => {
        const target = e.target as HTMLInputElement;
        handleFileSelect(target.files, requirement);
      };
      fileInputRef.current.click();
    }
  }, [handleFileSelect]);

  const renderDocumentSection = (requirement: DocumentRequirement) => {
    const docs = safeDocuments.filter(doc => doc.type === requirement.type);
    const hasRequiredDoc = docs.length > 0;
    const errorKey = `documents_${requirement.type}`;

    return (
      <Card key={requirement.type} className="p-4 border border-gray-200">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-md font-semibold text-gray-900 flex items-center">
              {requirement.name}
              {requirement.required && <span className="text-red-500 ml-1">*</span>}
              {hasRequiredDoc && <CheckCircleIcon className="w-5 h-5 text-green-500 ml-2" />}
            </h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">{requirement.description}</p>
          <p className="text-xs text-gray-500">
            Accepted formats: {requirement.acceptedTypes.map(type => type.split('/')[1]).join(', ')} • 
            Max size: {formatFileSize(requirement.maxSize)}
          </p>
        </div>

        {docs.length === 0 ? (
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onDrop={(e) => handleDrop(e, requirement)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !disabled && openFileDialog(requirement)}
          >
            <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">
              Drop file here or click to upload
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FileIcon className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(doc.size)} • Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      viewDocument(doc);
                    }}
                    disabled={disabled}
                    className="text-blue-600 hover:text-blue-700"
                    title="View document"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      downloadDocument(doc);
                    }}
                    disabled={disabled}
                    className="text-green-600 hover:text-green-700"
                    title="Download document"
                  >
                    <DownloadIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteClick(doc);
                    }}
                    disabled={disabled}
                    className="text-red-600 hover:text-red-700"
                    title="Delete document"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {/* Allow adding more documents */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                openFileDialog(requirement);
              }}
              disabled={disabled}
              className="w-full mt-2"
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              Upload Another Document
            </Button>
          </div>
        )}

        {errors[errorKey] && (
          <div className="mt-2 flex items-center text-red-600 text-sm">
            <AlertCircleIcon className="w-4 h-4 mr-1" />
            {errors[errorKey]}
          </div>
        )}
      </Card>
    );
  };

  if (requirements.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Document Requirements</h3>
      
      <div className="space-y-4">
        {requirements.length > 0 ? (
          requirements.map(requirement => renderDocumentSection(requirement))
        ) : (
          <div className="text-gray-500 italic">
            No document requirements available
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        disabled={disabled}
      />

      {/* Delete confirmation modal */}
      {deletingDocument && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              handleDeleteCancel();
            }
          }}
        >
          <div 
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking modal content
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{deletingDocument.filename}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteCancel();
                }}
                disabled={disabled}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteConfirm();
                }}
                disabled={disabled}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {imagePreview.document && (
        <DocumentPreviewModal
          isOpen={imagePreview.isOpen}
          onClose={() => setImagePreview({ isOpen: false, document: null })}
          documentUrl={imagePreview.document.url}
          fileName={imagePreview.document.filename}
          fileSize={imagePreview.document.size}
          mimeType={imagePreview.document.mimeType}
        />
      )}
    </div>
  );
}